import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useLocation, useMatch, Link } from 'react-router-dom';
import { I18nUtils } from '@medable/i18n-core';
import { Spinner, useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, PageWrapper, SectionCard } from 'libs.react';
import { ResponseDetailView } from '../../components/response_detail_view';
import ActivityResponseTitle from '../../components/visit_activity_responses/activity_response_title.component';
import { UserLocaleContext } from '../../contexts/user_locale/user_locale.context';
import { getParticipantNumber } from '../../helpers/participants.helpers';
import { getVisitActivityResponsesData } from '../../helpers/visit.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useSiteContext } from '../../hooks/use_site_context';
const DEFAULT_NON_MATCHING_LOCALE = 'en-US';
export const ParticipantResponseDetailPage = () => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const account = useAxonAccountContext();
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const siteUserLocale = useMemo(() => getUserSelectedLocale(), [getUserSelectedLocale]);
    const match = useMatch('/site/study/:studyId/participants/:participantId/response/:responseId');
    const participantId = match?.params.participantId;
    const responseId = match?.params.responseId;
    const studyId = match?.params.studyId;
    const location = useLocation();
    // This page expects navigation from the Responses table which sets router state.
    // Deep-link/refresh is not supported; when router state is missing, we fall back to en-US.
    const isClinicianInterview = location.state?.isClinicianInterview;
    const responseLocaleRaw = location.state?.responseLocale;
    // Recorded locale: the locale the task response was collected in (normalized to i18n format)
    const recordedLocale = useMemo(() => {
        return responseLocaleRaw ? I18nUtils.cortexToI18nFormat(responseLocaleRaw) : '';
    }, [responseLocaleRaw]);
    // Compute the locale to fetch response details with:
    // - Clinician interview: use recorded response locale (fallback to en-US if missing)
    // - Non-clinician: use recorded locale only if matches site user locale, otherwise en-US
    const queryLocale = useMemo(() => {
        const siteLocale = I18nUtils.cortexToI18nFormat(siteUserLocale) || DEFAULT_NON_MATCHING_LOCALE;
        if (isClinicianInterview) {
            // For clinician interviews, always use the recorded locale (locale the activity was completed in)
            return recordedLocale || DEFAULT_NON_MATCHING_LOCALE;
        }
        // For non-clinician: use recorded locale if it matches site user locale, otherwise en-US
        if (recordedLocale && siteLocale === recordedLocale) {
            return recordedLocale;
        }
        return DEFAULT_NON_MATCHING_LOCALE;
    }, [isClinicianInterview, recordedLocale, siteUserLocale]);
    const { data: responseData, isLoading } = useQuery({
        queryKey: ['participantResponseDetail', account._id, site?._id, participantId, responseId, queryLocale],
        queryFn: async () => {
            if (!environment || !site || !participantId || !responseId) {
                return null;
            }
            const response = await getVisitActivityResponsesData({
                environment,
                participantId,
                accountId: account._id,
                siteId: site._id,
                responseId,
                locale: queryLocale,
            });
            const allDetailedResponses = response?.data?.data || [];
            return allDetailedResponses[0] || null;
        },
        enabled: !!environment && !!account._id && !!site?._id && !!participantId && !!responseId && !!queryLocale,
    });
    if (isLoading) {
        return (_jsx(PageWrapper, { children: _jsx(Spinner, { wrapper: 'full' }) }));
    }
    if (!responseData) {
        const breadcrumbsPages = [
            { name: translate('generic.participants'), path: `/site/study/${studyId}/participants` },
            { name: translate('generic.responses'), path: '' },
        ];
        return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, title: '', LinkComponent: Link }), _jsx(SectionCard, { children: _jsx("section", { className: 'flex flex-col items-center justify-center p-10 m-10', children: _jsx("p", { className: 'text-md text-secondary', children: translate('info.participant_responses.empty') }) }) })] }));
    }
    const breadcrumbsPages = [
        { name: translate('generic.participants'), path: `/site/study/${studyId}/participants` },
        {
            name: getParticipantNumber(responseData.c_public_user),
            path: `/site/study/${studyId}/participants/${participantId}#responses`,
        },
        { name: responseData.c_task.c_name, path: '' },
    ];
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, title: _jsx(ActivityResponseTitle, { response: responseData }), LinkComponent: Link }), _jsx(ResponseDetailView, { response: responseData })] }));
};
